import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, CircleF, Autocomplete, MarkerF, PolygonF, PolylineF } from '@react-google-maps/api';
import CustomInput from '../../../components/customInput';
import Marker from '../../../components/marker';
import getMapDefinition from './mapsDefinition';
import config from '../../../config';
import utils from '../../../utils/site';
import {
  MIN_POLYGON_VERTICES,
  getPolygonPath,
  hasValidPolygon,
  toPointGeometry,
  toPolygonGeometry
} from './locationGeometry';

const Locations = ({ site, updateSite, createMode }) => {
  const [center, setCenter] = useState(null);
  const [latitude, setLatitude] = useState(site.latitude ? site.latitude : '');
  const [longitude, setLongitude] = useState(site.longitude ? site.longitude : '');
  const [countryCode, setCountryCode] = useState(site.countryCode ? site.countryCode : '');
  const [address, setAddress] = useState(site.address ? site.address : '');
  const [autocomplete, setAutocomplete] = useState(null);
  const [mapDefinition, setMapDefinition] = useState(getMapDefinition(center));
  const [mapInstance, setMapInstance] = useState(null);
  const initialPolygonPath = getPolygonPath(site.geometry);
  const [coverageMode, setCoverageMode] = useState(initialPolygonPath.length ? 'polygon' : 'radius');
  const [polygonPath, setPolygonPath] = useState(initialPolygonPath);
  const [polygonDraftPath, setPolygonDraftPath] = useState(
    initialPolygonPath.length < MIN_POLYGON_VERTICES ? initialPolygonPath : []
  );
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(
    initialPolygonPath.length > 0 && initialPolygonPath.length < MIN_POLYGON_VERTICES
  );
  const [isEditingPolygon, setIsEditingPolygon] = useState(false);
  const [isEditingArea, setIsEditingArea] = useState(createMode);
  const polygonInstance = useRef(null);
  const coverageRadius = Number(site.radius ?? site.type?.radius ?? 0);
  const numericCoverageRadius = Number.isFinite(coverageRadius) ? coverageRadius : 0;

  const isMounted = useRef(false);
  const hasInitialCenter = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      setMapDefinition(getMapDefinition(center));
      if (site.latitude && site.longitude) {
        if (site.latitude !== '' && site.longitude !== '') {
          setCenter({ lat: site.latitude, lng: site.longitude });
        }
      } else {
        setCenter(mapDefinition.defaultCenter);
      }
    }
    isMounted.current = true;
  }, []);

  useEffect(() => {
    if (center) {
      setLatitude(center.lat);
      setLongitude(center.lng);
      if (createMode || hasInitialCenter.current) fetchLocation(center.lat, center.lng);
      hasInitialCenter.current = true;
      setMapDefinition(getMapDefinition(center));
    }
  }, [center]);

  useEffect(() => {
    if (center) {
      updateSite({
        ...site,
        latitude: center.lat,
        longitude: center.lng,
        address,
        countryCode,
        ...(coverageMode === 'radius' ? { geometry: toPointGeometry(center) } : {})
      });
    }
  }, [center, address, countryCode]);

  const updateCenter = (value, coord) => {
    if (value !== '' && value !== null && value !== undefined && !Number.isNaN(Number(value))) {
      const numeric = Number(value);
      if (!center) {
        const newCenter = { lat: 0, lng: 0 };
        newCenter[coord] = numeric;
        setCenter(newCenter);
      } else {
        setCenter({ ...center, [coord]: numeric });
      }
    }
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const lat = autocomplete.getPlace().geometry.location.lat();
      const lng = autocomplete.getPlace().geometry.location.lng();
      setCenter({
        lat,
        lng
      });
      setAddress(autocomplete.getPlace().formatted_address);
    }
  };

  const handlePlusCode = address => {
    const plusCodeRegex = /^[A-Za-z0-9]{4}\+[A-Za-z0-9]{2}$/;
    const firstWord = address.split(' ')[0];
    const isPlusCode = plusCodeRegex.test(firstWord); //test if there is a plus code in front of address
    if (isPlusCode) {
      return address.substring(address.indexOf(' ') + 1);
    }
    return address;
  };

  const handleLocationName = addresses => {
    if (addresses.results.length > 0) {
      return handlePlusCode(addresses.results[0].formatted_address);
    }

    return null;
  };

  const fetchLocation = (lat, lng) => {
    if (lat && lng && window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      const latlng = {
        lat: lat,
        lng: lng
      };
      geocoder
        .geocode({ location: latlng })
        .then(response => {
          const address = handleLocationName(response);
          if (address) {
            setAddress(address);
            setCountryCode(utils.getCountryCode(response.results));
          } else {
            console.error(response.error_message);
          }
        })
        .catch(error => console.error(error));
    }
  };

  const updateGeometry = path => {
    setPolygonPath(path);
    updateSite({ ...site, geometry: toPolygonGeometry(path) });
  };

  const syncEditedPolygon = () => {
    if (!polygonInstance.current) return;

    const path = polygonInstance.current
      .getPath()
      .getArray()
      .map(point => ({ lat: point.lat(), lng: point.lng() }));
    updateGeometry(path);
  };

  const selectCoverageMode = mode => {
    setCoverageMode(mode);
    const shouldDrawPolygon = mode === 'polygon' && polygonPath.length < MIN_POLYGON_VERTICES;
    setIsDrawingPolygon(shouldDrawPolygon);
    setIsEditingPolygon(false);
    if (shouldDrawPolygon) setPolygonDraftPath(polygonPath);
    updateSite({
      ...site,
      geometry:
        mode === 'polygon'
          ? toPolygonGeometry(polygonPath)
          : toPointGeometry(center || { lat: Number(latitude), lng: Number(longitude) })
    });
  };

  const handleMapClick = event => {
    const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };

    if (coverageMode === 'polygon' && isEditingArea && isDrawingPolygon) {
      setPolygonDraftPath([...polygonDraftPath, point]);
      return;
    }

    setCenter(point);
  };

  const clearPolygon = () => {
    updateGeometry([]);
    setPolygonDraftPath([]);
    setIsEditingPolygon(false);
    setIsDrawingPolygon(true);
  };

  const finishPolygonDrawing = () => {
    if (polygonDraftPath.length < MIN_POLYGON_VERTICES) return;

    updateGeometry(polygonDraftPath);
    setPolygonDraftPath([]);
    setIsDrawingPolygon(false);
    setIsEditingPolygon(true);
  };

  const clearPolygonDraft = () => {
    setPolygonDraftPath([]);
  };

  const finishAreaEditing = () => {
    setPolygonDraftPath([]);
    setIsDrawingPolygon(false);
    setIsEditingPolygon(false);
    setIsEditingArea(false);
  };

  return (
    <>
      <h4 className="section-label">Select the location by typing the information or selecting on the map</h4>
      <div className="row">
        <div className="col-6">
          <div className="form-group">
            <label className={`${address === '' ? 'input-error' : ''}`}>Address</label>
            <Autocomplete onLoad={setAutocomplete} onPlaceChanged={onPlaceChanged}>
              <CustomInput
                id={'address'}
                placeholder={'Type an address'}
                isEditable={true}
                onChange={() => {}}
                value={address}
                icon={'location'}
              />
            </Autocomplete>
          </div>
        </div>
        <div className="col">
          <div className="form-group">
            <label className={`${latitude && latitude !== '' ? '' : 'input-error'}`}>Latitude</label>
            <CustomInput
              id={'latitude'}
              placeholder={'e.g. 25.7914'}
              isEditable={true}
              value={latitude}
              onChange={e => {
                const raw = e.target.value;
                const value = Number(raw);
                if (raw === '' || raw === '-' || raw === '+') {
                  setLatitude(raw);
                  return;
                }
                if (!Number.isNaN(value) && value >= -90 && value <= 90.99999) {
                  setLatitude(value);
                  updateCenter(value, 'lat');
                } else {
                  setLatitude(raw);
                }
              }}
              icon={'pin'}
            />
          </div>
        </div>
        <div className="col">
          <div className="form-group">
            <label className={`${longitude && longitude !== '' ? '' : 'input-error'}`}>Longitude</label>
            <CustomInput
              id={'longitude'}
              placeholder={'e.g. -80.1389'}
              isEditable={true}
              value={longitude}
              onChange={e => {
                const raw = e.target.value;
                const value = Number(raw);
                if (raw === '' || raw === '-' || raw === '+') {
                  setLongitude(raw);
                  return;
                }
                if (!Number.isNaN(value) && value >= -180 && value <= 180.99999) {
                  setLongitude(value);
                  updateCenter(value, 'lng');
                } else {
                  setLongitude(raw);
                }
              }}
              icon={'pin'}
            />
          </div>
        </div>
      </div>
      {!createMode && (
        <div className="area-edit-toolbar">
          {isEditingArea ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={coverageMode === 'polygon' && !hasValidPolygon(site.geometry)}
              onClick={finishAreaEditing}
            >
              <i className="uil uil-check" /> Done editing
            </button>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={() => setIsEditingArea(true)}>
              <i className="uil uil-pen" /> Edit area
            </button>
          )}
        </div>
      )}
      {isEditingArea && (
        <fieldset className="coverage-area">
          <legend>Coverage area</legend>
          <div className="coverage-area-controls">
            <div className="coverage-mode-selector" role="group" aria-label="Coverage area type">
              <button
                type="button"
                className={`btn ${coverageMode === 'radius' ? 'active' : ''}`}
                onClick={() => selectCoverageMode('radius')}
              >
                <i className="uil uil-circle" /> Radius
              </button>
              <button
                type="button"
                className={`btn ${coverageMode === 'polygon' ? 'active' : ''}`}
                onClick={() => selectCoverageMode('polygon')}
              >
                <i className="uil uil-polygon" /> Polygon
              </button>
            </div>
            {coverageMode === 'radius' ? (
              <div className="coverage-radius-details">
                <span>
                  Type: <strong>{site.type?.displayName || 'Not selected'}</strong>
                </span>
                <span>
                  Radius: <strong>{numericCoverageRadius} meters</strong>
                </span>
              </div>
            ) : (
              <div className="polygon-actions">
                {isDrawingPolygon ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={polygonDraftPath.length < MIN_POLYGON_VERTICES}
                      onClick={finishPolygonDrawing}
                    >
                      Finish polygon
                    </button>
                    {polygonPath.length < MIN_POLYGON_VERTICES && polygonDraftPath.length > 0 && (
                      <button type="button" className="btn btn-secondary" onClick={clearPolygonDraft}>
                        Clear points
                      </button>
                    )}
                  </>
                ) : isEditingPolygon ? (
                  <button type="button" className="btn btn-secondary" onClick={() => setIsEditingPolygon(false)}>
                    Finish editing
                  </button>
                ) : (
                  <button type="button" className="btn btn-secondary" onClick={() => setIsEditingPolygon(true)}>
                    Edit polygon
                  </button>
                )}
                {!isDrawingPolygon && polygonPath.length > 0 && (
                  <button type="button" className="btn btn-secondary" onClick={clearPolygon}>
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
          {coverageMode === 'polygon' && (
            <p
              className={`coverage-help ${
                !isDrawingPolygon || polygonDraftPath.length >= MIN_POLYGON_VERTICES ? '' : 'input-error'
              }`}
            >
              {isDrawingPolygon
                ? `Click the map to add at least ${MIN_POLYGON_VERTICES} points, then finish the polygon.`
                : isEditingPolygon
                ? 'Drag the polygon or its points to adjust the coverage area.'
                : 'Click Edit polygon to adjust the existing coverage area.'}
            </p>
          )}
        </fieldset>
      )}
      <div className={`site-map-wrapper ${isEditingArea && isDrawingPolygon ? 'drawing-polygon' : ''}`}>
        {coverageMode === 'polygon' && isEditingArea && isDrawingPolygon && (
          <div className="map-drawing-hint">
            <i className="uil uil-crosshairs" />
            <span>Click anywhere on the map to add a polygon point</span>
            <strong>
              {polygonDraftPath.length} {polygonDraftPath.length === 1 ? 'point' : 'points'}
            </strong>
          </div>
        )}
        <GoogleMap
          zoom={mapDefinition.zoom}
          center={mapDefinition.center}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            mapId: config.mapsApi.mapId,
            clickableIcons: !(isEditingArea && isDrawingPolygon),
            draggableCursor: isEditingArea && isDrawingPolygon ? 'crosshair' : undefined,
            draggingCursor: 'grabbing'
          }}
          mapContainerClassName="map-container"
          onClick={handleMapClick}
          onLoad={map => setMapInstance(map)}
        >
          <Marker map={mapInstance} position={mapDefinition.position}></Marker>
          {coverageMode === 'radius' && numericCoverageRadius > 0 && (
            <CircleF
              key={`${mapDefinition.position.lat}-${mapDefinition.position.lng}-${numericCoverageRadius}`}
              center={mapDefinition.position}
              radius={numericCoverageRadius}
              options={mapDefinition.circleOptions}
            />
          )}
          {coverageMode === 'polygon' && polygonPath.length > 0 &&
            (polygonPath.length >= MIN_POLYGON_VERTICES ? (
              <PolygonF
                path={polygonPath}
                options={{
                  ...mapDefinition.polygonOptions,
                  clickable: isEditingArea && isEditingPolygon && !isDrawingPolygon
                }}
                editable={isEditingArea && isEditingPolygon && !isDrawingPolygon}
                draggable={isEditingArea && isEditingPolygon && !isDrawingPolygon}
                onLoad={polygon => {
                  polygonInstance.current = polygon;
                }}
                onUnmount={() => {
                  polygonInstance.current = null;
                }}
                onMouseUp={syncEditedPolygon}
                onDragEnd={syncEditedPolygon}
              />
            ) : (
              <PolylineF path={polygonPath} options={{ ...mapDefinition.polygonOptions, clickable: false }} />
            ))}
          {coverageMode === 'polygon' && isEditingArea && isDrawingPolygon && polygonDraftPath.length > 0 &&
            (polygonDraftPath.length >= MIN_POLYGON_VERTICES ? (
              <PolygonF
                path={polygonDraftPath}
                options={{ ...mapDefinition.polygonOptions, clickable: false, fillOpacity: 0.12 }}
              />
            ) : (
              <PolylineF
                path={polygonDraftPath}
                options={{ ...mapDefinition.polygonOptions, clickable: false }}
              />
            ))}
          {coverageMode === 'polygon' &&
            isEditingArea &&
            isDrawingPolygon &&
            polygonDraftPath.map((point, index) => (
              <MarkerF
                key={`${point.lat}-${point.lng}-${index}`}
                position={point}
                clickable={false}
                zIndex={1000 + index}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 6,
                  fillColor: '#146ef5',
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeWeight: 2
                }}
              />
            ))}
        </GoogleMap>
      </div>
    </>
  );
};

export default Locations;
